import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  Snackbar,
  CircularProgress,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Chip
} from '@mui/material';
import { Email as EmailIcon, Send as SendIcon, CheckCircle as CheckCircleIcon, Assessment as AssessmentIcon } from '@mui/icons-material';

interface EmailStatus {
  initialized: boolean;
  hasApiKey: boolean;
  hasFromEmail: boolean;
  hasNotificationEmail: boolean;
}

interface TestEmailResponse {
  success: boolean;
  message?: string;
  status?: EmailStatus;
  error?: string;
  details?: string;
}

const TestEmailPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [emailStatus, setEmailStatus] = useState<EmailStatus | null>(null);
  const [response, setResponse] = useState<TestEmailResponse | null>(null);
  const [operationType, setOperationType] = useState<'test' | 'report'>('test');
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'info'
  });

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://us-central1-adriftchatbotv3.cloudfunctions.net';

  const checkEmailStatus = async () => {
    setStatusLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/emailStatus`);
      const data = await response.json();

      if (response.ok) {
        setEmailStatus(data.status);
        setSnackbar({
          open: true,
          message: 'Email service status retrieved successfully',
          severity: 'success'
        });
      } else {
        setSnackbar({
          open: true,
          message: data.error || 'Failed to get email status',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error checking email status:', error);
      setSnackbar({
        open: true,
        message: 'Failed to check email service status',
        severity: 'error'
      });
    } finally {
      setStatusLoading(false);
    }
  };

  const sendTestEmail = async () => {
    if (!email.trim()) {
      setSnackbar({
        open: true,
        message: 'Please enter an email address',
        severity: 'error'
      });
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setSnackbar({
        open: true,
        message: 'Please enter a valid email address',
        severity: 'error'
      });
      return;
    }

    setLoading(true);
    setOperationType('test');
    setResponse(null);

    try {
      const response = await fetch(`${API_BASE_URL}/testEmail`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ toEmail: email.trim() }),
      });

      const data = await response.json();
      setResponse(data);

      if (response.ok) {
        setSnackbar({
          open: true,
          message: data.message || 'Test email sent successfully!',
          severity: 'success'
        });
      } else {
        setSnackbar({
          open: true,
          message: data.error || 'Failed to send test email',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error sending test email:', error);
      setSnackbar({
        open: true,
        message: 'Network error occurred while sending test email',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const sendDailyReport = async () => {
    console.log('=== CLIENT: Starting daily report request ===');
    console.log('CLIENT: API_BASE_URL:', API_BASE_URL);
    console.log('CLIENT: Full URL:', `${API_BASE_URL}/sendDailyReport`);

    setLoading(true);
    setOperationType('report');
    setResponse(null);

    try {
      console.log('CLIENT: Making fetch request...');
      const response = await fetch(`${API_BASE_URL}/sendDailyReport`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      console.log('CLIENT: Response status:', response.status);
      console.log('CLIENT: Response headers:', Object.fromEntries(response.headers.entries()));

      const data = await response.json();
      console.log('CLIENT: Response data:', JSON.stringify(data, null, 2));

      setResponse(data);

      if (response.ok) {
        console.log('CLIENT: ✅ Daily report request successful');
        setSnackbar({
          open: true,
          message: data.message || 'Daily report sent successfully!',
          severity: 'success'
        });
      } else {
        console.log('CLIENT: ❌ Daily report request failed');
        setSnackbar({
          open: true,
          message: data.error || 'Failed to send daily report',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('CLIENT: ❌ Network error sending daily report:', error);
      const errorDetails = error instanceof Error ? {
        message: error.message,
        stack: error.stack,
        name: error.name
      } : { message: 'Unknown error', stack: undefined, name: 'Unknown' };
      console.error('CLIENT: Error details:', errorDetails);
      setSnackbar({
        open: true,
        message: 'Network error occurred while sending daily report',
        severity: 'error'
      });
    } finally {
      setLoading(false);
      console.log('=== CLIENT: Daily report request completed ===');
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const getStatusColor = (status: boolean) => status ? 'success' : 'error';
  const getStatusIcon = (status: boolean) => status ? <CheckCircleIcon /> : <EmailIcon />;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Test Email Service
        </Typography>
        <Button
          variant="outlined"
          onClick={checkEmailStatus}
          disabled={statusLoading}
          startIcon={statusLoading ? <CircularProgress size={20} /> : <EmailIcon />}
        >
          {statusLoading ? 'Checking...' : 'Check Status'}
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Email Service Status */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader
              title="Email Service Status"
              action={
                emailStatus && (
                  <Chip
                    label={emailStatus.initialized ? 'Configured' : 'Not Configured'}
                    color={emailStatus.initialized ? 'success' : 'error'}
                    size="small"
                  />
                )
              }
            />
            <CardContent>
              {emailStatus ? (
                <Box>
                  <Box display="flex" alignItems="center" mb={1}>
                    {getStatusIcon(emailStatus.hasApiKey)}
                    <Typography variant="body2" sx={{ ml: 1 }}>
                      SendGrid API Key: <Chip size="small" color={getStatusColor(emailStatus.hasApiKey)} label={emailStatus.hasApiKey ? 'Set' : 'Missing'} />
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" mb={1}>
                    {getStatusIcon(emailStatus.hasFromEmail)}
                    <Typography variant="body2" sx={{ ml: 1 }}>
                      From Email: <Chip size="small" color={getStatusColor(emailStatus.hasFromEmail)} label={emailStatus.hasFromEmail ? 'Set' : 'Missing'} />
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" mb={1}>
                    {getStatusIcon(emailStatus.hasNotificationEmail)}
                    <Typography variant="body2" sx={{ ml: 1 }}>
                      Notification Email: <Chip size="small" color={getStatusColor(emailStatus.hasNotificationEmail)} label={emailStatus.hasNotificationEmail ? 'Set' : 'Missing'} />
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Click "Check Status" to view the current email service configuration.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Send Test Email */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="Send Test Email" />
            <CardContent>
              <TextField
                fullWidth
                label="Recipient Email Address"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="test@example.com"
                margin="normal"
                helperText="Enter the email address where you want to receive the test email"
              />
              <Button
                fullWidth
                variant="contained"
                onClick={sendTestEmail}
                disabled={loading || !emailStatus?.initialized}
                startIcon={loading && operationType === 'test' ? <CircularProgress size={20} /> : <SendIcon />}
                sx={{ mt: 2 }}
              >
                {loading && operationType === 'test' ? 'Sending Test...' : 'Send Test Email'}
              </Button>

              <Button
                fullWidth
                variant="outlined"
                onClick={sendDailyReport}
                disabled={loading || !emailStatus?.initialized}
                startIcon={loading && operationType === 'report' ? <CircularProgress size={20} /> : <AssessmentIcon />}
                sx={{ mt: 1 }}
              >
                {loading && operationType === 'report' ? 'Generating Report...' : 'Send Daily Report'}
              </Button>

              {!emailStatus?.initialized && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Email service is not configured. Please check your SendGrid settings first.
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Response Display */}
        {response && (
          <Grid item xs={12}>
            <Card>
              <CardHeader
                title="Test Result"
                action={
                  <Chip
                    label={response.success ? 'Success' : 'Failed'}
                    color={response.success ? 'success' : 'error'}
                    size="small"
                  />
                }
              />
              <CardContent>
                {response.success ? (
                  <Alert severity="success">
                    <Typography variant="body2">
                      {response.message}
                    </Typography>
                  </Alert>
                ) : (
                  <Alert severity="error">
                    <Typography variant="body2" fontWeight="bold">
                      {response.error}
                    </Typography>
                    {response.details && (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {response.details}
                      </Typography>
                    )}
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TestEmailPage;
